import React, { useEffect, useState } from "react";
import { InformationCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { NetworkData } from "../../hooks/useNetworkData";
import { pathwayExplorerClient } from "../../services/PathwayExplorerClient";

interface PlotControlsDrawerProps {
  isPlotControlsOpen: boolean;
  networkData: NetworkData | null;
  selectedGeneToHighlight: string;
  setSelectedGeneToHighlight: (gene: string) => void;
  highlightColumn: string;
  setHighlightColumn: (col: string) => void;
  datasetId: string;
  labelFontSize: number;
  setLabelFontSize: (size: number) => void;
  splitComponents: boolean;
  setSplitComponents: (split: boolean) => void;
}

export const PlotControlsDrawer: React.FC<PlotControlsDrawerProps> = ({
  isPlotControlsOpen,
  networkData,
  selectedGeneToHighlight,
  setSelectedGeneToHighlight,
  highlightColumn,
  setHighlightColumn,
  datasetId,
  labelFontSize,
  setLabelFontSize,
  splitComponents,
  setSplitComponents,
}) => {
  const [columns, setColumns] = useState<string[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);

  useEffect(() => {
    const fetchColumns = async () => {
      if (!datasetId) {
        setColumns([]);
        return;
      }
      try {
        setIsLoadingColumns(true);
        const fetchedColumns: string[] = await pathwayExplorerClient.getPathwayDataColumns(datasetId);
        setColumns(fetchedColumns);
      } catch (error) {
        console.error("Error fetching pathway columns:", error);
        setColumns([]);
      } finally {
        setIsLoadingColumns(false);
      }
    };
    fetchColumns();
  }, [datasetId]);

  if (!networkData) return null;

  return (
    <div
      className={`absolute top-0 h-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 ${isPlotControlsOpen ? "border-l-0 rounded-r-lg" : "rounded-lg"
        } shadow-2xl transition-all duration-300 ease-in-out z-20 overflow-hidden`}
      style={{
        width: "320px",
        right: isPlotControlsOpen ? "0" : "-320px",
        opacity: isPlotControlsOpen ? 1 : 0,
        pointerEvents: isPlotControlsOpen ? "auto" : "none",
      }}
    >
      <div className="relative flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 z-20">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
            <InformationCircleIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
              Plot Controls
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Adjust network visualization settings
            </p>
          </div>
        </div>
      </div>

      <div
        className="relative p-5 space-y-6 overflow-y-auto bg-gray-50 dark:bg-slate-800/50 z-10"
        style={{ height: "calc(100% - 73px)" }}
      >
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Select Highlight Column
            </label>
            <div className="group relative">
              <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
              <div className="absolute right-0 top-full mt-2 hidden group-hover:block z-50 w-56 text-left">
                <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                  Choose which column to use for highlighting genes in the network
                  <div className="absolute bottom-full right-1 border-4 border-transparent border-b-gray-900 dark:border-b-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="relative">
            <select
              value={highlightColumn}
              onChange={(e) => setHighlightColumn(e.target.value)}
              disabled={isLoadingColumns}
              className="w-full pl-3 pr-8 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200 shadow-sm appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="none">None (no highlighting)</option>
              {isLoadingColumns ? (
                <option value="" disabled>Loading columns...</option>
              ) : (
                columns.map((col) => (
                  <option key={col} value={col}>
                    {col.startsWith("highlight_") ? col.replace(/^highlight_/, "") : col}
                  </option>
                ))
              )}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Search Gene
            </label>
            <div className="group relative">
              <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 w-56 text-left">
                <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                  Select a gene to highlight in the graph
                  <div className="absolute top-full right-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="relative">
            <select
              value={selectedGeneToHighlight}
              onChange={(e) => setSelectedGeneToHighlight(e.target.value)}
              className="w-full pl-3 pr-8 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200 shadow-sm appearance-none"
            >
              <option value="none">None</option>
              {networkData.nodes.map((node) => (
                <option key={node.id} value={node.label}>
                  {node.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Label Font Size
            </label>
            <div className="group relative">
              <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 w-48 text-left">
                <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                  Adjust node label font size
                  <div className="absolute top-full right-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setLabelFontSize(Math.max(6, labelFontSize - 1))}
              className="flex-1 py-2 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors flex items-center justify-center border-r border-gray-200 dark:border-slate-600"
            >
              <span className="text-gray-600 dark:text-gray-300 font-bold text-lg">
                −
              </span>
            </button>
            <div className="flex-1 py-2 text-center bg-gray-50 dark:bg-slate-700/50">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {labelFontSize}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setLabelFontSize(Math.min(24, labelFontSize + 1))}
              className="flex-1 py-2 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors flex items-center justify-center border-l border-gray-200 dark:border-slate-600"
            >
              <span className="text-gray-600 dark:text-gray-300 font-bold text-lg">
                +
              </span>
            </button>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label 
                className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer"
                onClick={() => setSplitComponents(!splitComponents)}
              >
                Split by Connected Components
              </label>
              <div className="group relative">
                <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 w-56 text-left">
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg py-2 px-3 shadow-lg relative whitespace-normal break-words">
                    Separate the largest connected component from disconnected genes
                    <div className="absolute top-full right-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only"
                checked={splitComponents}
                onChange={(e) => setSplitComponents(e.target.checked)}
              />
              <div
                onClick={() => setSplitComponents(!splitComponents)}
                className={`block w-10 h-6 rounded-full transition-colors cursor-pointer shadow-inner ${
                  splitComponents ? "bg-blue-500" : "bg-gray-300 dark:bg-slate-600"
                }`}
              ></div>
              <div
                onClick={() => setSplitComponents(!splitComponents)}
                className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform cursor-pointer shadow ${
                  splitComponents ? "transform translate-x-4" : ""
                }`}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
